/**
 * Create form for a partner.
 */
import React, { useState } from 'react';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { Input } from '@/components/forms/Input';
import { Textarea } from '@/components/forms/Textarea';
import { AddressAutocompleteInput } from '@/components/forms/AddressAutocompleteInput';
import { DepartmentSelect } from '@/components/common/DepartmentSelect';
import { useCreatePartner } from '@/hooks/usePartners';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  applyPartnerAddressSelection,
  applyPartnerFormField,
  buildCreatePartnerDto,
  emptyCreatePartnerForm,
  isPartnerFormValid,
} from '@/components/partners/partner-form-utils';
import type { PartnerFormValues } from '@/types/partner.types';

/** Props for {@link CreatePartnerView}. */
interface CreatePartnerViewProps {
  onClose: () => void;
  onSuccess: () => void;
}

/** Create form for a partner. */
export const CreatePartnerView: React.FC<Readonly<CreatePartnerViewProps>> = ({
  onClose,
  onSuccess,
}) => {
  const createMutation = useCreatePartner();
  const [formData, setFormData] = useState<PartnerFormValues>(emptyCreatePartnerForm);

  /** Patch one string field. */
  const handleFieldChange = (field: keyof PartnerFormValues, value: string) => {
    setFormData((current) => applyPartnerFormField(current, field, value));
  };

  /** Validate name, POST create DTO, then close. */
  const handleSubmit = async () => {
    if (!isPartnerFormValid(formData)) {
      toast.error(getErrorMessage('requiredPartnerName'));
      return;
    }

    try {
      await createMutation.mutateAsync(buildCreatePartnerDto(formData));
      toast.success('Partenaire créé avec succès !');
      onSuccess();
      onClose();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer un partenaire"
      subtitle="Renseignez les informations du partenaire"
      onClose={onClose}
      onSubmit={handleSubmit}
      isSubmitting={createMutation.isPending}
      isValid={isPartnerFormValid(formData)}
      submitLabel="Créer"
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Informations de base</h3>

          <Input
            label="Nom du partenaire"
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            required
            placeholder="Ex: HomeClasse Convention"
            maxLength={255}
          />

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="Description du partenaire..."
            rows={3}
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Adresse</h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AddressAutocompleteInput
              label="Adresse"
              value={formData.address}
              onChange={(value) => handleFieldChange('address', value)}
              onSelection={(result) =>
                setFormData((current) => applyPartnerAddressSelection(current, result))
              }
              placeholder="Ex: 12 rue de la République, Paris"
              size="sm"
            />
            <Input
              label="Code postal"
              value={formData.zipCode}
              onChange={(e) => handleFieldChange('zipCode', e.target.value)}
              placeholder="Ex: 75001"
              maxLength={10}
              inputSize="sm"
            />
            <Input
              label="Ville"
              value={formData.city}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              placeholder="Ex: Paris"
              maxLength={100}
              inputSize="sm"
            />
            <Input
              label="Pays"
              value={formData.country}
              onChange={(e) => handleFieldChange('country', e.target.value)}
              placeholder="Ex: FR"
              maxLength={100}
              inputSize="sm"
            />
            <DepartmentSelect
              label="Département"
              value={formData.department}
              onChange={(val) => handleFieldChange('department', val)}
              placeholder="Sélectionner"
              inputSize="sm"
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Contact</h3>

          <Input
            label="Site web"
            type="url"
            value={formData.website}
            onChange={(e) => handleFieldChange('website', e.target.value)}
            placeholder="https://example.com"
            maxLength={255}
          />

          <Input
            label="Email de contact"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleFieldChange('contactEmail', e.target.value)}
            placeholder="contact@example.com"
            maxLength={255}
          />

          <Input
            label="Téléphone de contact"
            type="tel"
            value={formData.contactPhone}
            onChange={(e) => handleFieldChange('contactPhone', e.target.value)}
            placeholder="+33 1 23 45 67 89"
            maxLength={50}
          />
        </div>
      </div>
    </SlidePanelForm>
  );
};
